import React, { SyntheticEvent, useEffect, useState } from 'react';
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Box,
  CircularProgress,
  LinearProgress,
} from '@mui/material';

import { UseFormik } from '@/utils/Formik/types';

import HelperText from '@/components/HelperText/HelperText';
import { Type } from '@/components/HelperText/types';
import { ErrorIcon } from '@/components/TextField/styles';

import {
  Container,
  Label,
  LabelContainer,
  ListOptions,
  StarSymbol,
  StyledTextField,
} from './styles';

export interface SelectProps {
  id?: string;
  optionText?: string | number;
  optionValue?: string | number;
}

interface AutoCompleteProps {
  id: string;
  label?: string;
  value?: SelectProps;
  inputValue?: string;
  dataCy?: string;
  loading?: boolean;
  loadingType?: 'bar' | 'spinner';
  disabled?: boolean;
  required?: boolean;
  disableStarSymbol?: boolean;
  helperText?: string;
  placeholder?: string;
  resetInput?: boolean;
  options?: SelectProps[];
  labelClassName?: string;
  formik?: UseFormik<any>;
  syncOption?: boolean;
  setSyncOption?: (value: boolean) => void;
  defaultSelected?: SelectProps;
  setDefaultSelected?: (value: SelectProps) => void;
  disableHelperText?: boolean;
  error?: boolean;
  submit?: 'text' | 'value';
  onInputChange?: (
    event: SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void;
}

const DropdownAutocomplete: React.FC<AutoCompleteProps> = ({
  id,
  label,
  value,
  inputValue,
  formik,
  required,
  disableStarSymbol = false,
  helperText,
  placeholder,
  dataCy = '',
  options = [],
  disabled = false,
  resetInput = false,
  loading = undefined,
  loadingType = 'bar',
  labelClassName = '',
  defaultSelected = {
    optionText: '',
    optionValue: '',
  },
  setDefaultSelected,
  disableHelperText = false,
  syncOption,
  setSyncOption,
  error = false,
  onInputChange,
  submit = 'value',
}) => {
  const [open, setOpen] = useState<boolean>(false);

  const [newValue, setNewValue] = useState<SelectProps>(defaultSelected);

  const isHelperTextError = formik?.touched[id] && formik?.errors[id] !== '' && formik?.errors[id];

  const helperTextValue = () => {
    if (!disableHelperText && isHelperTextError) {
      return (
        <>
          <ErrorIcon />
          {formik?.errors[id]}
        </>
      );
    }

    return helperText;
  };

  const helperTextType = isHelperTextError ? Type.error : Type.primary;

  const selectedValueObject =
    options.find((option) =>
      submit === 'text'
        ? option.optionText === formik?.values[id]
        : String(option.optionValue) === String(formik?.values[id]),
    ) || null;

  const handleChange = (_event: SyntheticEvent, selectedOption: SelectProps | null) => {
    let valueToSubmit = '';

    if (selectedOption) {
      if (submit === 'text') {
        valueToSubmit = String(selectedOption.optionText ?? '');
      } else {
        valueToSubmit = String(selectedOption.optionValue ?? '');
      }
    }

    formik?.setFieldValue(id, valueToSubmit);
    formik?.setFieldTouched(id, true, false);
  };

  useEffect(() => {
    if (resetInput) {
      setNewValue({
        optionText: '',
        optionValue: '',
      });
    }
  }, [resetInput]);

  useEffect(() => {
    if (setDefaultSelected) {
      setDefaultSelected(newValue);
    }
  }, [value]);

  useEffect(() => {
    if (formik && options.length && syncOption) {
      const current = formik.values[id];
      const match = options.find((o) => o.optionValue === String(current));
      setNewValue(
        match || {
          optionText: '',
          optionValue: '',
        },
      );

      if (setSyncOption) setSyncOption(false);
    }
  }, [syncOption, options]);

  const getOptionLabel = (option: SelectProps | string) => {
    if (typeof option === 'object' && option && option.optionText) {
      return String(option.optionText);
    }
    if (typeof option === 'string') {
      return option;
    }
    return '';
  };

  return (
    <Container>
      <LabelContainer>
        {required && !disableStarSymbol && <StarSymbol>*</StarSymbol>}
        <Label className={labelClassName} htmlFor={id}>
          {label}
        </Label>
        {loading && loadingType === 'spinner' && (
          <CircularProgress size={14} style={{ marginLeft: '8px', color: '#000' }} />
        )}
      </LabelContainer>
      <Autocomplete
        id={id}
        data-cy={dataCy}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        disabled={loading ? true : disabled}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        value={selectedValueObject}
        defaultValue={value}
        onChange={handleChange}
        options={options}
        renderOption={(props, option) => (
          <ListOptions {...props} key={option.optionValue}>
            {option.optionText}
          </ListOptions>
        )}
        sx={{
          borderRadius: '10px',
        }}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        onInputChange={onInputChange}
        noOptionsText="Nenhuma opção encontrada"
        inputValue={inputValue}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            variant="outlined"
            size="small"
            placeholder={placeholder}
            required={required}
            error={error}
            disabled={disabled}
            onFocus={() => {
              formik?.setFieldTouched(id, true, false);
            }}
            onChange={() => {
              formik?.setFieldTouched(id, true, false);
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && loadingType === 'bar' && (
                    <Box
                      sx={{
                        bottom: 1,
                        left: '50%',
                        width: '98%',
                        overflow: 'hidden',
                        position: 'absolute',
                        transform: 'translateX(-50%)',
                        borderRadius: '0 0 50px 50px',
                      }}
                    >
                      <LinearProgress />
                    </Box>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {!!helperTextValue() && <HelperText type={helperTextType}>{helperTextValue()}</HelperText>}
    </Container>
  );
};

export default DropdownAutocomplete;
